/**
 * Audit Dashboard Table Component
 * Refactored to use Dashboard context instead of React Hook Form
 *
 * Progressive Loading Strategy:
 * - Phase 1: Shows table immediately after /api/invoices/search/invoice-data responds
 * - Phase 2: Merges charge data from /api/invoices/search/charge-data in background
 * - This ensures fast initial render without waiting for all APIs
 */

import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { InvoiceDto } from 'src/generated/services/TFinancialApi';
import _ from 'lodash';
import { FILTERABLE_COLUMN_KEYS } from './constants';
import MassActions from 'src/presentations/components/molecules/table/MassActions';
import { useTableContext } from 'src/presentations/components/molecules/table/State';
import { useDefaultColumns, useTableInvoice } from 'src/presentations/components/molecules/table/NewInvoiceTable';
import { UnifiedTablePagination } from 'src/presentations/components/molecules/table/UnifiedTablePagination';
import { TableMain } from 'src/presentations/components/molecules/table/TableComponents';
import { useDashboard } from '../../pages/dashboardPage/DashboardContext';
import { useColumnVisibility } from '../../molecules/table/columnSettings';
import DashboardMassActions from './MassActions';
import { AuditColumnHeader } from './AuditColumnHeader';

// Helper function to map UI column keys to API column enum
const mapAccessorKeyToApiColumn = (accessorKey: string): string | undefined => {
  const mapping: Record<string, string> = {
    invoiceNumber: 'INVOICE_NUMBER',
    status: 'STATUS',
    billedEntity: 'BILLED_ENTITY',
    incoterm: 'INCOTERM',
    shipper: 'SHIPPER',
    consignee: 'CONSIGNEE',
    service: 'SERVICE',
    shipmentType: 'SHIPMENT_TYPE',
    tradeLane: 'TRADE_LANE',
    shipmentNumber: 'SHIPMENT_NUMBER',
    date: 'DATE',
  };

  // Return undefined for sort-only columns (no backend metadata support)
  if (accessorKey === 'totalAmount' || accessorKey === 'deviationAmount') {
    return undefined;
  }

  return mapping[accessorKey];
};

const getRowId = (row: InvoiceDto) => row?.id;
type TableRowSelection = [Record<string, any>, Dispatch<SetStateAction<Record<string, any>>>];
// eslint-disable-next-line react-refresh/only-export-components
export const useTableRowSelection = (): TableRowSelection => {
  const { selectedItems } = useTableContext();
  const [rowSelection, setRowSelection] = useState({});

  /**
   * Sync global state to local state
   * */
  useEffect(() => {
    const newRowSelection = selectedItems?.reduce((acc, cur) => ({ ...acc, [getRowId(cur)]: true }), {});

    if (!_.isEqual(newRowSelection, rowSelection)) {
      setRowSelection(newRowSelection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems]);

  return [rowSelection, setRowSelection];
};

export const AuditDashboardTable = () => {
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get('invoiceId') || '';

  // Get all data from Dashboard context
  const { items, totalItems, filters, page, pageSize, onTableFilter, onPageChange, onPageSizeChange, onSort, isLoadingInvoiceData } = useDashboard();

  // Column visibility management
  const { columns: columnConfigs } = useColumnVisibility();

  // Local state for table
  const [pageIndex, setPageIndex] = useState(page - 1);
  const [sorting, setSorting] = useState<any[]>([]);

  // Sync page index with context
  useEffect(() => {
    setPageIndex(page - 1);
  }, [page]);

  // Get currency and date reference from context filters
  const { currencyReference, dateReference } = filters;

  // Use default columns
  const columns = useDefaultColumns(currencyReference, dateReference);

  // Handle column filter changes - use context handler
  const handleTableFilter = useCallback(
    (columnId: string, selectedValues: string[]) => {
      onTableFilter(columnId, selectedValues);
    },
    [onTableFilter]
  );

  // Sorting handler - use context handler
  const handleSortingChange = useCallback(
    (updatedSorting: any) => {
      setSorting(updatedSorting);
      if (updatedSorting.length > 0) {
        const { id, desc } = updatedSorting[0];
        const method = desc ? 'desc' : 'asc';
        onSort(method, id);
      }
    },
    [onSort]
  );

  // Pagination handlers - use context handlers
  const handlePageChange = useCallback(
    (newPage: number) => {
      setPageIndex(newPage - 1);
      onPageChange(newPage);
    },
    [onPageChange]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setPageIndex(0);
      onPageSizeChange(newPageSize);
    },
    [onPageSizeChange]
  );

  // Enhance default columns to include filter UI for specific fields with lazy loading
  // and filter/sort by visibility settings
  const enhancedColumns = useMemo(() => {
    // Create a map of default columns for easy lookup
    const defaultColumnsMap = new Map(columns.map((col) => [col.accessorKey as string, col]));

    // Use columnConfigs to determine order and visibility
    return columnConfigs
      .filter((config) => config.isVisible && defaultColumnsMap.has(config.id))
      .map((config) => {
        const col = defaultColumnsMap.get(config.id);
        const { accessorKey } = col as any;

        // Only add filter UI for filterable columns
        if (!accessorKey || typeof accessorKey !== 'string' || !FILTERABLE_COLUMN_KEYS.has(accessorKey)) {
          return col;
        }

        return {
          ...col,
          Header: ({ column }) => (
            <AuditColumnHeader
              key={`${column.id}-${filters.invoiceStatusCategory}`}
              column={column}
              accessorKey={accessorKey}
              customHeader={
                typeof col.Header === 'function'
                  ? col.Header({
                      column,
                      header: undefined,
                      table: undefined,
                    })
                  : col.Header
              }
              filters={filters}
              keyword={keyword}
              dateReference={dateReference}
              onSort={onSort}
              onTableFilter={handleTableFilter}
            />
          ),
        };
      });
  }, [columns, columnConfigs, filters, dateReference, onSort, handleTableFilter]);

  const columnOrder = useMemo(() => enhancedColumns.map((c) => (c.accessorKey as string) || c.id || ''), [enhancedColumns]);

  const table = useTableInvoice({
    columns: enhancedColumns,
    data: items ?? [],
    getRowId,
    enableSorting: false,
    state: {
      // CRITICAL: Only wait for Phase 1 (invoice data) before showing table
      // Phase 2 (charge data) loads in background and merges progressively
      isLoading: isLoadingInvoiceData,
      sorting,
      pagination: { pageIndex, pageSize },
      columnOrder,
    },
    onSortingChange: handleSortingChange,
    manualPagination: true,
    enablePagination: true,
    enableStickyHeader: true,
    renderEmptyRowsFallback: () => <p className='text-center text-16 font-medium uppercase text-neutral-40 py-30'>NO DATA AVAILABLE</p>,
    enableColumnPinning: true,
    initialState: { columnPinning: { right: ['pendingItems', 'actions'] } },
    muiTableHeadProps: {
      sx: {
        '& th:last-child': {
          boxShadow: '-4px 0px 12px 0px rgba(89, 114, 130, 0.08)',
          borderLeft: '1px solid #E5E7EB',
        },
      },
    },
    muiTableBodyProps: {
      sx: {
        '& td:last-child': {
          boxShadow: '-4px 0px 12px 0px rgba(89, 114, 130, 0.08)',
          borderLeft: '1px solid #E5E7EB',
        },
      },
    },
  });

  return (
    <div className='w-full h-full flex flex-col min-h-0'>
      <TableMain
        table={table}
        tableContainerProps={{
          sx: {
            height: '100%',
            maxHeight: '100%',
            overflow: 'auto',
            '& .MuiTable-root': {
              borderCollapse: 'separate',
            },
          },
        }}
      />
      <div className='sticky bottom-0 px-12 py-0 xl:p-16 bg-neutral-10 flex items-center justify-between border-t border-neutral-20 shrink-0 w-full min-h-[60px] shipping-service-pagination'>
        <UnifiedTablePagination
          pageIndex={pageIndex}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={[25, 50, 100]}
        />
      </div>
      {/* Mass Actions - Floating bar with dashboard actions */}
      <MassActions onRenderActions={() => <DashboardMassActions />} />
    </div>
  );
};
